'use client';

import type React from 'react';
import { useCallback, useEffect } from 'react';
import { Button } from 'reactstrap';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';

import { formatRelativeTime, generateRandomString } from 'src/helpers/utils';
import { fetchMessages, fetchMessageThreads } from 'src/redux/jobs/api';
import { clearMessages, updateMessage } from 'src/redux/jobs/slice';

interface Thread {
  customer_chat_group_id?: number;
  reference_no?: string;
  unread_chat_count?: number;
  last_message_at?: string;
  service_category?: string;
  [key: string]: unknown;
}

const SideBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const threads = useAppSelector(
    (state) => (state?.jobDetails?.messages as Record<string, unknown>)?.threads,
  ) as Thread[] | undefined;
  const activeThreadId = useAppSelector(
    (state) => (state?.jobDetails?.messages as Record<string, unknown>)?.activeThreadId,
  ) as number | undefined;
  const shouldRefetchGroups = useAppSelector(
    (state) => (state?.jobDetails?.messages as Record<string, unknown>)?.shouldRefetchGroups,
  ) as boolean | undefined;

  const threadCount = useAppSelector(
    (state) => (state?.jobDetails?.data?.customer_message as Record<string, unknown>)?.multiple_thread_count,
  ) as number | undefined;

  const serviceRequestId = useAppSelector(
    (state) => state?.jobDetails?.data
      ?.service_request_id,
  );
  const jobId = useAppSelector(
    (state) => state?.jobDetails?.data?.root_node_id,
  );
  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
  const isFetchingMessageThreads = useAppSelector(
    (state) => state?.jobDetails?.isFetchingMessageThreads,
  );
  const fetchGroups = useCallback(
    () => {
      dispatch(fetchMessageThreads(serviceRequestId)(
        type === 'jobDetails'
          ? { job_id: jobId } : {},
      )).unwrap().catch(
        (e: Record<string, unknown>) => {
          toast(
            (e?.error as string)
            || (e?.errors as Record<string, string[]>)
              ?.general?.[0]
            || 'Something went wrong',
            { type: 'error' },
          );
        },
      );
    },
    [dispatch, jobId, serviceRequestId, type],
  );

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);
  useEffect(() => {
    if (shouldRefetchGroups) {
      fetchGroups();
    }
  }, [fetchGroups, shouldRefetchGroups]);

  const onThreadClick = useCallback(
    (thread: Thread) => {
      dispatch(clearMessages());
      dispatch(updateMessage({
        activeThreadId: thread?.customer_chat_group_id,
      }));
      const chatGroupId = thread?.customer_chat_group_id != null
        ? String(thread.customer_chat_group_id)
        : undefined;
      dispatch(fetchMessages(serviceRequestId, chatGroupId)({}))
        .unwrap().catch((e: Record<string, unknown>) => {
          dispatch(updateMessage({
            activeThreadId: '',
          }));
          toast(
            (e?.error as string)
            || (e?.errors as Record<string, string[]>)
              ?.general?.[0]
            || 'Something went wrong',
            { type: 'error' },
          );
        });
    },
    [dispatch, serviceRequestId],
  );

  return (
    <div className="message-sidebar">
      {threads?.map((thread) => (
        <Button
          color="none"
          className={[
            'message-sidebar-button',
            thread?.customer_chat_group_id
              === activeThreadId ? 'active' : '',
          ].join(' ')}
          onClick={() => onThreadClick(thread)}
          key={thread?.customer_chat_group_id}
        >
          <div className="message-sidebar-wrapper">
            <div className="message-sidebar-id-wrapper">
              {thread?.reference_no
                ? (
                  <div className="message-sidebar-id">
                    #
                    {thread?.reference_no}
                  </div>
                ) : ''}
              {thread?.unread_chat_count
                ? <div className="message-sidebar-status" />
                : ''}
            </div>
            {thread?.last_message_at
              ? (
                <div className="message-sidebar-time">
                  {formatRelativeTime(
                    thread?.last_message_at,
                  )}
                </div>
              ) : ''}
          </div>
          {thread?.service_category
            ? <div className="message-sidebar-title">{thread?.service_category}</div>
            : ''}
        </Button>
      ))}
      {isFetchingMessageThreads && threadCount && Array.from({ length: threadCount }).map(
        () => (
          <Button
            key={generateRandomString()}
            className="message-sidebar-button"
            color="none"
          >
            <div className="message-sidebar-wrapper">
              <div className="common-loading w-44" />
              <div className="common-loading w-28" />
            </div>
            <div className="common-loading" />
          </Button>
        ),
      )}
    </div>
  );
};

export default SideBar;